import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Bot, X, Send, Loader2 } from "lucide-react";
import { API, WA_DEFAULT } from "@/data/content";
import XLogo from "./XLogo";

const WELCOME = {
  role: "assistant",
  content:
    "Welcome to the X-INTEL desk. Ask me anything about markets, emerging technology, the companies we study, or TEAM X consultation services. Educational insights only — capital is always at risk.",
};

export default function FloatingWidgets() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("teamx-session");
    if (!s) {
      s = crypto.randomUUID();
      localStorage.setItem("teamx-session", s);
    }
    return s;
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const payload = p.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const j = JSON.parse(payload);
            if (j.delta) {
              setMsgs((m) => {
                const copy = [...m];
                const lastMsg = copy[copy.length - 1];
                copy[copy.length - 1] = { ...lastMsg, content: lastMsg.content + j.delta };
                return copy;
              });
            }
          } catch {
            /* partial chunk */
          }
        }
      }
    } catch {
      setMsgs((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Connection issue. Please reach us directly on WhatsApp at +1 260 568 7097.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed bottom-5 right-5 z-[120] flex flex-col items-end gap-3">
        <AnimatePresence>
          {open && (
            <motion.div
              data-testid="ai-chat-panel"
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="flex h-[460px] w-[min(92vw,370px)] flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl shadow-black/40"
            >
              <div className="flex items-center gap-3 border-b border-line bg-panel px-4 py-3.5">
                <XLogo size={26} />
                <div className="flex-1">
                  <p className="font-display text-lg font-bold uppercase leading-none tracking-wide text-ink">
                    X-Intel Assistant
                  </p>
                  <p className="mt-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-up">
                    <span className="pulse-dot h-1 w-1 rounded-full bg-up" />
                    Online — Educational Desk
                  </p>
                </div>
                <button
                  data-testid="ai-chat-close-button"
                  onClick={() => setOpen(false)}
                  aria-label="Close assistant"
                  className="text-dim transition-colors hover:text-brand"
                >
                  <X size={17} />
                </button>
              </div>

              <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
                {msgs.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-xs leading-relaxed sm:text-sm ${
                        m.role === "user"
                          ? "bg-brand text-white"
                          : "border border-line bg-panel text-mute"
                      }`}
                    >
                      {m.content ||
                        (busy && i === msgs.length - 1 ? (
                          <Loader2 size={14} className="animate-spin text-brand" />
                        ) : null)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 border-t border-line bg-panel p-3">
                <input
                  data-testid="ai-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about markets, Tesla, services..."
                  className="flex-1 rounded-lg border border-line bg-surface px-3.5 py-2.5 text-xs text-ink outline-none transition-colors placeholder:text-dim focus:border-brand sm:text-sm"
                />
                <button
                  data-testid="ai-chat-send-button"
                  onClick={send}
                  disabled={busy}
                  aria-label="Send message"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand text-white transition-colors hover:bg-brand-crimson disabled:opacity-60"
                >
                  <Send size={15} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          data-testid="floating-ai-chat-trigger"
          onClick={() => setOpen((o) => !o)}
          aria-label="Open AI assistant"
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          className="flex h-13 w-13 items-center justify-center rounded-full border border-brand/50 bg-panel text-brand shadow-lg shadow-brand/20"
          style={{ height: 52, width: 52 }}
        >
          {open ? <X size={20} /> : <Bot size={20} />}
        </motion.button>

        <motion.a
          data-testid="floating-whatsapp-trigger"
          href={WA_DEFAULT}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Contact on WhatsApp"
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          className="relative flex items-center justify-center rounded-full bg-wa text-white shadow-lg shadow-wa/25"
          style={{ height: 52, width: 52 }}
        >
          <MessageCircle size={21} />
          <span className="pulse-red absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-brand font-mono text-[9px] font-bold">
            1
          </span>
        </motion.a>
      </div>
    </>
  );
}
